import { Dispatch, useCallback } from 'react';
import { getFilter, saveFilter } from '../service/CommunityFilterService';
import { FilterAction } from './useCommunityFilter';
import { CommunityFilter } from '../model/CommunityFilter';
import { useCommunityFilterDispatch } from '../provider/CommunityFilterDispatchProvider';
import { PlaceAutoCompleteResult } from '../model/Places';
import { placeDetails, reverseGeoCode } from '../service/PlaceService';

export const useResetFilter = (
  communityFilterDispatch: Dispatch<FilterAction>,
) => {
  return useCallback(async () => {
    const defaultCommunityFilter = await getFilter(true);
    console.debug('Default community filter: ', defaultCommunityFilter);
    communityFilterDispatch({
      type: 'RESET_FILTER',
      payload: defaultCommunityFilter,
    });
  }, [communityFilterDispatch]);
};

export const useApplyFilter = (communityFilter: CommunityFilter) => {
  const communityFilterDispatch = useCommunityFilterDispatch();

  return useCallback(async () => {
    await saveFilter(communityFilter);
    communityFilterDispatch({
      type: 'APPLY_FILTER',
    });
  }, [communityFilterDispatch, communityFilter]);
};

export const useChangeDistance = () => {
  const communityFilterDispatch = useCommunityFilterDispatch();
  return useCallback(
    (event: Event, newValue: number | number[]) => {
      communityFilterDispatch({
        type: 'CHANGE_DISTANCE',
        payload: newValue as number,
      });
    },
    [communityFilterDispatch],
  );
};

export const useChangeAgeRange = () => {
  const communityFilterDispatch = useCommunityFilterDispatch();
  return useCallback(
    (event: Event, newValue: number | number[]) => {
      const [start, end] = newValue as number[];
      communityFilterDispatch({
        type: 'CHANGE_AGE_RANGE',
        payload: {
          ageRangeStartInMonths: start,
          ageRangeEndInMonths: end,
        },
      });
    },
    [communityFilterDispatch],
  );
};

export const useChangeLocation = () => {
  const communityFilterDispatch = useCommunityFilterDispatch();
  return useCallback(
    async (typeahead: PlaceAutoCompleteResult) => {
      const placeId = typeahead.placeId;
      try {
        const placeDetail = await placeDetails(placeId);
        communityFilterDispatch({
          type: 'CHANGE_LOCATION',
          payload: placeDetail,
        });
      } catch (e) {
        console.debug(`Cannot get place detail from place id ${placeId}`);
      }
    },
    [communityFilterDispatch],
  );
};

export const useCurrentLocation = (
  onSuccessGetPositionCallback: (position: GeolocationPosition) => void,
  onErrorGetPositionCallback: (errorPosition: GeolocationPositionError) => void,
) => {
  const communityFilterDispatch = useCommunityFilterDispatch();
  const successGetPosition = useCallback(
    async (position: GeolocationPosition) => {
      const [longitude, latitude] = [
        position.coords.longitude,
        position.coords.latitude,
      ];
      try {
        const place = await reverseGeoCode(latitude, longitude);
        if (place) {
          communityFilterDispatch({
            type: 'CHANGE_LOCATION',
            payload: place,
          });
        }
        onSuccessGetPositionCallback(position);
      } catch (e) {
        console.debug(
          `Cannot get current location with longitude ${longitude},latitude ${latitude}`,
        );
      }
    },
    [communityFilterDispatch, onSuccessGetPositionCallback],
  );
  const errorGetPosition = useCallback(
    (error: GeolocationPositionError) => {
      console.debug(error);
      onErrorGetPositionCallback(error);
    },
    [onErrorGetPositionCallback],
  );
  return useCallback(async () => {
    navigator.geolocation.getCurrentPosition(
      successGetPosition,
      errorGetPosition,
      { timeout: 30_000 },
    );
  }, [successGetPosition, errorGetPosition]);
};
